/*
 * Copyright © 2018-2024, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactNode, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import moment from "moment";
import { TimeZoneResponse } from "../../../models/time/TimeZoneResponse";
import { AddressRequest } from "../../../models/contact/AddressRequest";
import { LocationRequest } from "../../../models/clinic/LocationRequest";
import GeoApi from "../../../api/GeoApi";
import { LocationDTO } from "../../../models/geo/LocationDTO";
import {
  getGeneralError,
  removeTrailingDot,
} from "../../../util/helperFunctions";
import CombinedSelect from "../../ReactHookFormFields/General/Select/CombinedSelect";
import TimeZoneApi from "../../../api/TimeZoneApi";
import { strings } from "../../../common/Strings/Strings";
import AlertBox from "../../AlertBox";
import { Pencil } from "../../../common/Icons/Pencil";
import Button from "../../Button";

interface Props {
  address?: AddressRequest;
  countryName?: string;
  changeAddress(): void;
  setLocalizedAddress(
    request: LocationRequest,
    onError?: (error: any) => void,
    onFinally?: () => void
  ): void;
}

interface AddressLocationForm {
  timeZone: TimeZoneResponse[] | null;
}

const AddressMapCheck: React.FC<Props> = ({
  address,
  countryName,
  changeAddress,
  setLocalizedAddress,
}: Props) => {
  const [coordinates, setCoordinates] = useState<LocationDTO | null>(null);
  const [timeZones, setTimeZones] = useState<TimeZoneResponse[]>([]);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<AddressLocationForm>({
    mode: "onChange",
  });

  const getAddressString = (): ReactNode => {
    const isAddressValid = !!(
      address?.street &&
      address?.city &&
      address?.countryCode &&
      address?.zip
    );

    if (isAddressValid) {
      const formattedAddress = `${removeTrailingDot(address.street)}, ${
        address.city
      }`;
      const countryAndZip = `${countryName} ${address.zip}`;

      return (
        <>
          {address.houseDetails && (
            <>
              {address.houseDetails}
              <br />
            </>
          )}
          {formattedAddress}
          <br />
          {countryAndZip}
        </>
      );
    }

    return <AlertBox message={`${strings.invalidAddress}!`} />;
  };

  useEffect(() => {
    if (!address) return;

    const transformAddresses = async (addresses: AddressRequest[]) => {
      if (!addresses) return;
      try {
        const googleResponse = await GeoApi.getGeoFromAddresses(addresses);
        const { data } = googleResponse;
        if (
          Array.isArray(data) &&
          Array.isArray(data[0]) &&
          data[0].length !== 0
        ) {
          const location = GeoApi.getLocationFromResponse(
            googleResponse.data[0]
          );
          setCoordinates(location);
        } else {
          setCoordinates(null);
        }
      } catch {
        setCoordinates(null);
      }
    };

    void transformAddresses([address]);
  }, [address]);

  useEffect(() => {
    const getTimeZones = async () => {
      try {
        const resp = await TimeZoneApi.getTimeZones();
        setTimeZones(resp.data);
      } catch (err) {
        setError(await getGeneralError(err));
      }
    };

    void getTimeZones();
  }, []);

  useEffect(() => {
    if (!address) return;

    const guessedTimeZone = timeZones.find((t) =>
      t.zoneId.includes(address.city)
    );

    const defaultTimeZone = timeZones.find((t) =>
      t.zoneId.includes(moment.tz.guess())
    );

    if (guessedTimeZone) {
      setValue("timeZone", [guessedTimeZone]);
    } else if (defaultTimeZone) {
      setValue("timeZone", [defaultTimeZone]);
    }
  }, [address, setValue, timeZones]);

  const onError = async (err: any) => {
    setError(await getGeneralError(err));
  };

  const onFinally = () => {
    setLoading(false);
  };

  const submit = ({ timeZone }: AddressLocationForm) => {
    if (!timeZone) return;

    setLoading(true);
    const request: LocationRequest = {
      zoneId: timeZone[0].zoneId,
      ...(coordinates
        ? { latitude: coordinates.lat, longitude: coordinates.lng }
        : {}),
    };
    setLocalizedAddress(request, onError, onFinally);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between space-x-4">
        <span className="font-semibold text-zinc-800 dark:text-zinc-300">
          {getAddressString()}
        </span>
        <Button
          aria-label={strings.edit}
          className="tw-icon"
          onClick={changeAddress}
          variant="link"
        >
          <span className="sr-only">{strings.edit}</span>
          <Pencil />
        </Button>
      </div>
      <form className="space-y-6" onSubmit={handleSubmit(submit)}>
        <CombinedSelect
          allowNew={false}
          control={control}
          isTypeahead
          label={strings.timeZone}
          labelKey="zoneId"
          multiple={false}
          name="timeZone"
          options={timeZones}
          placeholder={strings.chooseFromWithDots}
          required
          showRequired
        />
        <AlertBox message={error} />
        <AlertBox
          hidden={errors.timeZone?.type !== "required"}
          message={strings.pleaseFillRequiredFields}
        />
        <Button
          disabled={isSubmitting || loading}
          loading={loading}
          type="submit"
          variant="primary"
        >
          {strings.saveAndContinue}
        </Button>
      </form>
    </div>
  );
};

export default AddressMapCheck;
